import { Form, Tabs } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { documentApi } from '../../../../api/apiClient';
import { DocumentMultiReceiptEntity, DocumentReceiptEntity } from '../../../../generated-api';
import { useDocument } from '../../../../store/useDocumentListStore';
import { notifyError, notifySuccess } from '../../../../utils/notification';
import { FormComponentProps } from '../../defs/types';
import { ReceiptFormInputs } from '../ReceiptFormInputs';
import { IndividualDeleteButton } from './IndividualDeleteButton';

const { TabPane } = Tabs;

const RightAlignedButton = styled.div`
  display: inline-block;
  margin-left: auto;
  float: right;
`;

export function MultiReceiptFormComponent({
  changed,
  documentId,
  entity,
  groupId,
  setChanged,
}: FormComponentProps<DocumentMultiReceiptEntity>) {
  const [tab, setTab] = useState('0');
  const form = Form.useFormInstance();
  const { setPatchedDocument } = useDocument(groupId, documentId);

  const formEntities: DocumentReceiptEntity[] = useMemo(
    () =>
      entity.receipts.map((receipt) => ({
        type: receipt.type,
        payment_date: receipt.payment_date,
        payee: receipt.payee,
        business_number: receipt.business_number,
        receipt_details: receipt.receipt_details,
      })),
    [entity.receipts]
  );

  const onConfirmDelete = useCallback(
    async (index: number) => {
      try {
        const updatedReceipts = entity.receipts.filter((_, i) => i !== index);
        const response = await documentApi.documentControllerPatchDocument(groupId, documentId, {
          edited_ocr_result: {
            ...entity,
            receipts: updatedReceipts,
          },
        });
        setPatchedDocument(response.data);

        // 明細以外は initialValues で管理しているため、フォームの値を更新しないと画面に反映されない
        updatedReceipts.forEach((receipt, i) => {
          form.setFields([
            {
              name: ['resultReceipts', i, 'payment_date'],
              value: receipt.payment_date,
            },
            {
              name: ['resultReceipts', i, 'business_number'],
              value: receipt.business_number,
            },
            {
              name: ['resultReceipts', i, 'payee_input'],
              value: receipt.payee?.[0],
            },
          ]);
        });

        notifySuccess('読取り結果を削除しました。');

        // 削除後、タブの位置を調整
        const currentTabIndex = parseInt(tab);
        if (currentTabIndex >= updatedReceipts.length) {
          // 最後のタブを削除した場合は、新しい最後のタブに移動
          setTab((updatedReceipts.length - 1).toString());
        } else if (currentTabIndex > index) {
          // 削除したタブより後ろにいる場合は、インデックスを1つ減らす
          setTab((currentTabIndex - 1).toString());
        }
      } catch (error) {
        console.error('Failed to delete receipt:', error);
        notifyError('読取り結果の削除に失敗しました。');
      }
    },
    [entity, groupId, documentId, setPatchedDocument, tab, form]
  );

  return (
    <>
      <Tabs activeKey={tab} onChange={setTab}>
        {formEntities.map((form, index) => {
          return (
            <TabPane key={index} forceRender tab={index + 1} tabKey={index.toString()}>
              <ReceiptFormInputs
                key={`${index}-${form.payment_date}-${form.payee}`} // 各領収書の一意なキーを生成（支払日と支払先の組み合わせ）
                changed={changed}
                documentId={documentId}
                entity={form}
                groupId={groupId}
                path={['resultReceipts', index]}
                setChanged={setChanged}
              />
              <RightAlignedButton>
                <IndividualDeleteButton index={index} onConfirmDelete={onConfirmDelete} />
              </RightAlignedButton>
            </TabPane>
          );
        })}
      </Tabs>
    </>
  );
}
